import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiHome,
  FiShoppingCart,
  FiPackage,
  FiBox,
  FiDollarSign,
  FiRotateCcw,
  FiTool,
  FiTrendingDown,
  FiUsers,
  FiBarChart2,
  FiLogOut,
  FiSearch,
  FiBell,
  FiChevronDown,
  FiMenu,
  FiX,
  FiShoppingBag,
} from "react-icons/fi";
import { logout } from "../../features/auth/authSlice";

const navigationGroups = [
  {
    title: "Main",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: FiHome },
      { path: "/pos", label: "Point of Sale", icon: FiShoppingCart },
    ],
  },
  {
    title: "Inventory",
    items: [
      { path: "/products", label: "Products", icon: FiPackage },
      { path: "/stock", label: "Stock", icon: FiBox },
      { path: "/stock/overview", label: "Stock Overview", icon: FiBarChart2 },
      { path: "/material-purchases", label: "Material Purchases", icon: FiShoppingBag },
    ],
  },
  {
    title: "Sales & Services",
    items: [
      { path: "/sales", label: "Sales", icon: FiDollarSign },
      { path: "/returns", label: "Returns", icon: FiRotateCcw },
      { path: "/service-orders", label: "Services", icon: FiTool },
    ],
  },
  {
    title: "Management",
    items: [
      { path: "/expenses", label: "Expenses", icon: FiTrendingDown },
      { path: "/customers", label: "Customers", icon: FiUsers },
      { path: "/reports", label: "Reports", icon: FiBarChart2 },
    ],
  },
];

export default function AppShell_Premium() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      dispatch(logout());
      navigate("/login");
    }
  };

  return (
    <div className="premium-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 99,
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`premium-sidebar ${sidebarOpen ? "open" : ""}`}>
        {/* Logo */}
        <div className="premium-logo">
          <div className="premium-logo-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" fillOpacity="0.9"/>
              <path d="M2 17L12 22L22 17V12L12 17L2 12V17Z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <div className="premium-logo-text">FlowManage</div>
            <div className="premium-logo-subtitle">Jewellery ERP</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="premium-nav">
          {navigationGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="premium-nav-group">
              <div className="premium-nav-group-title">{group.title}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `premium-nav-item ${isActive ? "active" : ""}`
                    }
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="premium-nav-icon" />
                    <span className="premium-nav-label">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="premium-sidebar-footer">
          <div className="premium-business-card">
            <div style={{ fontWeight: 600, marginBottom: "4px", color: "#3B82F6", fontSize: "0.8rem" }}>
              💼 ROHA Jewellery
            </div>
            <div style={{ fontSize: "0.65rem", color: "#64748B" }}>
              Better management, stronger results
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="premium-main">
        {/* Header */}
        <header className="premium-header" style={{ height: "64px", padding: "0 24px" }}>
          {/* Mobile Menu Button */}
          <button
            className="premium-mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              display: "none",
              width: 40,
              height: 40,
              border: "1px solid var(--border-color)",
              background: "transparent",
              borderRadius: "8px",
              cursor: "pointer",
              color: "var(--text-primary)",
            }}
          >
            {sidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </button>

          {/* Global Search */}
          <div className="premium-search" style={{ maxWidth: "450px" }}>
            <FiSearch className="premium-search-icon" size={16} />
            <input
              type="text"
              className="premium-search-input"
              placeholder="Search by name, SKU, or barcode..."
              style={{ height: "40px", fontSize: "0.875rem" }}
            />
          </div>

          {/* Header Actions */}
          <div className="premium-header-actions">
            {/* Notifications */}
            <button className="premium-notification-btn" style={{ width: "36px", height: "36px" }}>
              <FiBell size={18} />
              <span className="premium-notification-badge" />
            </button>

            {/* User Menu */}
            <div
              className="premium-user-menu"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              style={{ position: "relative", padding: "6px 12px" }}
            >
              <div className="premium-user-avatar" style={{ width: "32px", height: "32px", fontSize: "0.875rem" }}>
                {user?.name?.charAt(0)?.toUpperCase() || "W"}
              </div>
              <div className="premium-user-info">
                <div className="premium-user-name" style={{ fontSize: "0.8125rem" }}>
                  {user?.name || "Wase Admin"}
                </div>
                <div className="premium-user-role" style={{ fontSize: "0.6875rem" }}>
                  {user?.role || "Administrator"}
                </div>
              </div>
              <FiChevronDown size={14} style={{ color: "var(--text-muted)" }} />

              {/* User Dropdown */}
              {userMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    right: 0,
                    background: "var(--bg-white)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    boxShadow: "var(--shadow-lg)",
                    minWidth: 180,
                    zIndex: 1000,
                  }}
                >
                  <button
                    onClick={handleLogout}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      color: "var(--error)",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    <FiLogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="premium-content">
          <Outlet />
        </main>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .premium-mobile-menu-btn {
            display: flex !important;
            align-items: center;
            justify-content: center;
          }
          .premium-sidebar {
            position: fixed;
            z-index: 100;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }
          .premium-sidebar.open {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
